import { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';

import { FilePicker } from '@@/form-components/FilePicker/FilePicker';
import { FilePickerSkeleton } from '@@/form-components/FilePicker/FilePickerSkeleton';
import { buildFileTree } from '@@/form-components/FilePicker/utils';
import { Widget } from '@@/Widget';
import { CollapseExpandButton } from '@@/CollapseExpandButton';
import { Input } from '@@/form-components/Input';
import { FormControl } from '@@/form-components/FormControl';

import { artifactsStepValidationSchema } from '../validation';
import { DeploymentType, detectDeploymentType } from '../detectDeploymentType';
import { WizardStep } from '../WizardContext';
import { useSearch } from '../../../queries/useSearch';
import { FormValues } from '../type';
import { useSource } from '../../../sources/queries/useSource';
import { RefSelector } from '../../../RefField/RefSelector';
import { useGitRefs } from '../../../queries/useGitRefs';
import { SourceId } from '../../../sources/types';

import { StepTitle } from './StepTitle';

export const ArtifactsStep: WizardStep = {
  id: 'artifacts',
  label: 'Artifacts & stacks',
  Component,
  validateStep,
};

function Component() {
  const { values, setValues } = useFormikContext<FormValues>();
  const sourceId = values.sources[0];

  return (
    <>
      <StepTitle
        title="Configure artifacts & stacks"
        subtitle="Browse your source and select files to create the stack"
      />

      <FormControl inputId="artifact-name" label="Name" required>
        <Input
          id="artifact-name"
          onChange={(e) =>
            setValues((prev) => ({
              ...prev,
              artifacts: [
                {
                  ...prev.artifacts[0],
                  name: e.target.value,
                },
              ],
            }))
          }
          placeholder="my-stack"
          value={values.artifacts[0].name}
          data-cy="artifact-name-input"
          required
        />
      </FormControl>

      <SourcePicker sourceId={sourceId} />
    </>
  );
}

function SourcePicker({ sourceId }: { sourceId: SourceId }) {
  const [gitRef, setGitRef] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const { values, setValues } = useFormikContext<FormValues>();
  const sourceQuery = useSource(sourceId);
  const refsQuery = useGitRefs({ sourceId });
  const searchQuery = useSearch(
    { sourceId, reference: gitRef },
    { enabled: !refsQuery.isLoading }
  );

  const fileTree = useMemo(
    () => buildFileTree(searchQuery.data || []),
    [searchQuery.data]
  );

  const isLoading =
    sourceQuery.isLoading || refsQuery.isLoading || searchQuery.isLoading;

  return (
    <Widget>
      <Widget.Taskbar className="!mb-0 flex items-center gap-2">
        <CollapseExpandButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        />
        {!sourceQuery.isLoading && <span>{sourceQuery.data?.name}</span>}
        <div className="ml-auto">
          <RefSelector
            inputId="ref-field"
            value={gitRef}
            onChange={(ref) => {
              setGitRef(ref);
              setValues((prev) => ({
                ...prev,
                artifacts: [
                  {
                    ...prev.artifacts[0],
                    files: prev.artifacts[0].files.map((file) => ({
                      ...file,
                      ref,
                    })),
                  },
                ],
              }));
            }}
            sourceId={sourceId}
            size="sm"
          />
        </div>
      </Widget.Taskbar>
      {isExpanded && (
        <Widget.Body>
          {isLoading && <FilePickerSkeleton />}
          {!isLoading && (
            <FilePicker
              files={fileTree}
              onChange={(files) =>
                setValues((prev) => {
                  const artifactFiles = files.map((path) => ({
                    sourceId,
                    path: path.startsWith('/') ? path.replace('/', '') : path,
                    ref: gitRef,
                  }));

                  return {
                    ...prev,
                    artifacts: [
                      {
                        ...prev.artifacts[0],
                        files: artifactFiles,
                        deploymentType: artifactFiles[0]
                          ? detectDeploymentType(artifactFiles[0].path)
                          : DeploymentType.Kubernetes,
                      },
                    ],
                  };
                })
              }
              value={values.artifacts[0].files.map(({ path }) => `/${path}`)}
              disableDirSelect
              disableMultiSelect
            />
          )}
        </Widget.Body>
      )}
    </Widget>
  );
}

function validateStep() {
  return artifactsStepValidationSchema();
}
